import classNames from 'classnames';
import type { ReactNode } from 'react';
import styled, { keyframes } from 'styled-components';

import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { LoadingIndicator } from '#src/atoms/loader/LoadingIndicator/index.tsx';
import { LabelS } from '#src/atoms/typography/index.tsx';
import { Chip } from '#src/atoms/ui/Chip/index.tsx';
import { LiveDot } from '#src/components/live/LiveDot/index.tsx';

const Wrapper = styled(ButtonBase)`
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: var(--double-unit);
`;

const glowCircleAnimation = keyframes`
  0% {
    box-shadow:
      0px 0px 20px 0px rgba(var(--orb-color), 0.6),
      inset 0px 0px 32px 0px rgba(var(--orb-color), 0.3),
      inset 0px 0px 8px 0px rgba(var(--orb-color), 0.4),
      inset 0px 0px 4px 1px rgba(var(--orb-color), 0.8);
  }
  50% {
    box-shadow:
      0px 0px 24px 0px rgba(var(--orb-color), 0.6),
      inset 0px 0px 36px 0px rgba(var(--orb-color), 0.3),
      inset 0px 0px 10px 0px rgba(var(--orb-color), 0.4),
      inset 0px 0px 6px 1px rgba(var(--orb-color), 0.8),
      0px 0px 20px 0px rgba(var(--orb-color), 0.6),
      inset 0px 0px 32px 0px rgba(var(--orb-color), 0.3),
      inset 0px 0px 8px 0px rgba(var(--orb-color), 0.4),
      inset 0px 0px 4px 1px rgba(var(--orb-color), 0.8);
  }
  100% {
    box-shadow:
      0px 0px 20px 0px rgba(var(--orb-color), 0.6),
      inset 0px 0px 32px 0px rgba(var(--orb-color), 0.3),
      inset 0px 0px 8px 0px rgba(var(--orb-color), 0.4),
      inset 0px 0px 4px 1px rgba(var(--orb-color), 0.8);
  }
`;

const GlowCircle = styled(Horizontal).attrs({ gap: 0, center: true })`
  width: calc(6 * var(--unit));
  height: calc(6 * var(--unit));
  border-radius: 50%;
  background: rgba(14, 14, 14, 0.54);
  backdrop-filter: blur(20px);
  box-shadow:
    0px 0px 20px 0px rgba(var(--orb-color), 0.6),
    inset 0px 0px 32px 0px rgba(var(--orb-color), 0.3),
    inset 0px 0px 8px 0px rgba(var(--orb-color), 0.4),
    inset 0px 0px 4px 1px rgba(var(--orb-color), 0.8);
  position: relative;
  .disabled & {
    box-shadow: 0px 0px 0px 2px var(--c-nd-300);
    background: transparent;
  }
  .loading & {
    animation: ${glowCircleAnimation} 2.5s ease-in-out infinite;
  }
`;

const Positionned = styled.div`
  position: absolute;
  left: 50%;
  top: 55%;
  transform: translate(-50%, -50%);
  z-index: 1;
`;

const NotificationDot = styled.div`
  position: absolute;
  right: calc(-1 * var(--unit));
  top: var(--half-unit);
`;

const Badge = styled(Chip)`
  margin-top: calc(-1 * var(--unit));
`;

type Props = {
  label: ReactNode;
  colors: [number, number, number];
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  children?: ReactNode;
  showNotificationDot?: boolean;
};

export const Orb = ({
  label,
  colors,
  onClick,
  disabled,
  loading,
  children,
  showNotificationDot = false,
}: Props) => {
  return (
    <Wrapper
      onClick={onClick}
      disabled={disabled || loading}
      className={classNames({ disabled, loading })}
      style={{ '--orb-color': colors.join(', ') } as React.CSSProperties}
    >
      {!!loading && (
        <Positionned>
          <LoadingIndicator smaller />
        </Positionned>
      )}
      <GlowCircle>{children}</GlowCircle>
      {!disabled && !!showNotificationDot && (
        <NotificationDot>
          <LiveDot animate size="md" />
        </NotificationDot>
      )}
      <Badge>
        <LabelS bold>{label}</LabelS>
      </Badge>
    </Wrapper>
  );
};
